import Avatar from '@mui/material/Avatar';
import { CaretDownIcon, PlusIconBlue } from 'assets/icons';
import { CustomModal, If } from 'components';
import { NotifierTypes } from 'constants/notifierConstants';
import { MerchantTeam } from 'containers/app/app.model';
import {
  disableHoverState,
  initialBannerState,
  selectCurrentTeam,
  selectHoverState,
  selectMenuState,
  selectMerchantTeams,
  selectUserDetails,
  setCurrentTeam,
  showNotifier,
  updateBannersData
} from 'containers/app/appSlice';
import { resetInvoiceState } from 'containers/invoices/InvoicesSlice';
import { useAppDispatch, useAppSelector } from 'hooks';
import React, { FC, Fragment, useState } from 'react';
import { VoidFn } from 'types/baseTypes';
import { getInitials } from 'utils/commonUtils';
import { getTeamSubtitle } from 'containers/side-nav/utils';
import ListModal from './ListModal';
import { isTablet } from 'react-device-detect';
import { resetPayoutState } from 'containers/payouts/payoutsSlice';
import { useNavigate } from 'react-router-dom';
import routesPath from 'routes/RoutesPath';
import { useCommunicationsControl } from 'containers/communications';
import CreateNewTeamPopup from '../../../containers/create-team/components/CreateNewTeamPopup';

interface TeamSwitcherProps {
  className?: string;
  showDetails?: boolean;
  onStartTeamSwitch?: VoidFn;
  isOverlayedSideNavLayout?: boolean;
  onTeamSwitchClose?: VoidFn;
}

const TeamSwitcher: FC<TeamSwitcherProps> = ({
  isOverlayedSideNavLayout,
  className,
  showDetails = false,
  onStartTeamSwitch = () => null,
  onTeamSwitchClose = () => null
}) => {
  const isMenuActive = useAppSelector(selectMenuState);
  const isHoverActive = useAppSelector(selectHoverState);
  const merchantTeams = useAppSelector(selectMerchantTeams);
  const activeTeam = useAppSelector(selectCurrentTeam);
  const userDetails = useAppSelector(selectUserDetails);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { allowAction: allowCreateTeam } = useCommunicationsControl('CREATE_TEAM');
  const [open, toggleOpen] = useState<boolean>(false);
  const [showCreateTeamPopup, setShowCreateTeamPopup] = useState<boolean>(false);
  const teamName = activeTeam?.name;

  const onTeamSelect = (item: MerchantTeam) => {
    dispatch(resetInvoiceState());
    dispatch(resetPayoutState());
    dispatch(setCurrentTeam(item));
    dispatch(updateBannersData(initialBannerState));
    showTeamSwitchNotifier(item);
    toggleOpen(false);
    onTeamSwitchClose();
    window?.lendica?.ibranch?.destroy();
  };

  const showTeamSwitchNotifier = team => {
    const message = `<div>
        Viewing team <b>'${team?.name}'</b>
      </div>`;
    dispatch(
      showNotifier({
        message: {
          primaryMessage: message,
          secondaryMessage: team?.role,
          iconSrc: team?.profileImageUrl,
          iconInitials: getInitials(team?.name),
          isMessageHtml: true
        },
        type: NotifierTypes.CUSTOM,
        showClose: false,
        bgStyle: 'bg-[#3E556D] rounded-md',
        fontStyle: 'text-primary font-normal',
        shouldAnimate: true
      })
    );
  };

  const initiateTeamSwitch = () => {
    onStartTeamSwitch();
    dispatch(disableHoverState());
    toggleOpen(true);
  };

  const onExitTeamSwitch = () => {
    toggleOpen(false);
    onTeamSwitchClose();
  };

  const handleCreateTeamClick = () => {
    if (!allowCreateTeam()) {
      return;
    }
    setShowCreateTeamPopup(true);
    toggleOpen(false);
  };

  const handleCreateTeamKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    handleCreateTeamClick();
  };

  const handleCreateTeamContinue = () => {
    setShowCreateTeamPopup(false);
    onTeamSwitchClose();
    navigate(routesPath.CREATE_TEAM);
  };

  const handleCreateTeamClose = () => {
    setShowCreateTeamPopup(false);
    onTeamSwitchClose();
  };

  return (
    <Fragment>
      <div className={`${className} z-30 flex cursor-pointer flex-row items-center gap-2`} onClick={initiateTeamSwitch}>
        <Avatar
          onClick={initiateTeamSwitch}
          src={activeTeam?.profileImageUrl}
          className={`h-10 w-10 bg-gradient-to-r from-[#D8DFE5] to-[#A4D7FA] ${open && 'ring-2 ring-secondary '}`}>
          <div className="mt-0.5 text-px13 font-semibold leading-5 text-[#333333]">{getInitials(teamName)}</div>
        </Avatar>

        <If condition={showDetails}>
          <If condition={isMenuActive || (isHoverActive && !isTablet) || isOverlayedSideNavLayout}>
            <div>
              <div className="max-w-[135px] truncate text-sbase font-semibold leading-5 text-[#333333]">
                {activeTeam?.name}
              </div>
              <div className="mt-0.5 max-w-[135px] truncate text-px13 text-primaryText">
                {getTeamSubtitle(activeTeam?.role, activeTeam?.label)}
              </div>
            </div>
          </If>
          <CaretDownIcon onClick={initiateTeamSwitch} className="ml-auto cursor-pointer" />
        </If>
      </div>

      <CustomModal
        closeModal={onExitTeamSwitch}
        onOutsideClick={onExitTeamSwitch}
        open={open}
        backgroundColor="bg-primary"
        width="md:min-w-[375px]">
        <ListModal
          onItemSelection={onTeamSelect}
          selectedItem={activeTeam}
          title="Switch Teams"
          list={merchantTeams}
          onClose={onExitTeamSwitch}
        />
        <div
          role="button"
          tabIndex={0}
          aria-label="Create New Team"
          className="flex cursor-pointer flex-row items-center border-t border-borderGray py-3 px-[17px]"
          onClick={handleCreateTeamClick}
          onKeyDown={handleCreateTeamKeyDown}>
          <PlusIconBlue className="h-5 w-5" />
          <div className="pl-2 text-sbase font-semibold text-secondary">Create New Team</div>
        </div>
      </CustomModal>

      <CreateNewTeamPopup
        open={showCreateTeamPopup}
        onClose={handleCreateTeamClose}
        onContinue={handleCreateTeamContinue}
      />
    </Fragment>
  );
};

export default TeamSwitcher;
